import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, LifeBuoy, Search, ChevronDown, Loader2 } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { getCustomerKnowledge } from "@/lib/api";

export function CustomerHelp({ open, onClose, onAsk }) {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    getCustomerKnowledge().then(setArticles).catch(() => {}).finally(() => setLoading(false));
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return articles;
    return articles.filter(
      (a) => a.title.toLowerCase().includes(q) || (a.resolution || "").toLowerCase().includes(q)
    );
  }, [articles, query]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/60" onClick={onClose} data-testid="customer-help-panel">
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="absolute right-0 top-0 h-full w-[26rem] max-w-[90vw] bg-[var(--surface-2)] glass border-l border-[var(--border)] flex flex-col"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 px-4 h-14 border-b border-[var(--border)] shrink-0">
              <LifeBuoy size={15} className="text-[var(--primary)]" />
              <span className="font-display font-semibold text-sm flex-1">Help &amp; Guides</span>
              <button data-testid="customer-help-close" onClick={onClose}
                className="h-8 w-8 grid place-items-center rounded-lg text-[var(--text-muted)] hover:text-white hover:bg-white/5 transition-colors"><X size={18} /></button>
            </div>

            <div className="p-3 border-b border-[var(--border)] shrink-0">
              <div className="flex items-center gap-2 rounded-xl bg-black/30 border border-[var(--border)] px-3 py-2">
                <Search size={14} className="text-[var(--text-muted)]" />
                <input data-testid="customer-help-search" value={query} onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search guides (password, email, printer…)"
                  className="flex-1 bg-transparent text-sm outline-none placeholder:text-[var(--text-muted)]" />
              </div>
            </div>

            <ScrollArea className="flex-1">
              <div className="p-3 space-y-2">
                {loading && <div className="flex items-center gap-2 text-sm text-[var(--text-muted)] p-2"><Loader2 size={15} className="animate-spin" /> Loading guides…</div>}
                {!loading && filtered.length === 0 && <p className="text-xs text-[var(--text-muted)] p-2">No guides match that. Try asking Apex in the chat.</p>}
                {filtered.map((a) => {
                  const expanded = openId === a.id;
                  return (
                    <div key={a.id} className="rounded-xl border border-[var(--border)] bg-[var(--surface)] overflow-hidden" data-testid="help-article">
                      <button data-testid="help-article-toggle" onClick={() => setOpenId(expanded ? null : a.id)}
                        className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-white/5 transition-colors">
                        <span className="text-sm font-semibold flex-1">{a.title}</span>
                        {a.category && <span className="text-[10px] font-mono-tech px-2 py-0.5 rounded bg-black/30 border border-[var(--border)] text-[var(--text-muted)] uppercase">{a.category}</span>}
                        <ChevronDown size={15} className={`text-[var(--text-muted)] transition-transform ${expanded ? "rotate-180" : ""}`} />
                      </button>
                      {expanded && (
                        <div className="px-3 pb-3 pt-1 border-t border-[var(--border)]">
                          <p className="text-sm text-[var(--text-secondary)] whitespace-pre-line">{a.resolution}</p>
                          {onAsk && (
                            <button data-testid="help-article-ask" onClick={() => { onAsk(a.title); onClose(); }}
                              className="mt-2.5 text-xs font-semibold text-[var(--primary)] hover:underline">
                              Ask Apex to walk me through this →
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                <p className="text-[11px] text-[var(--text-muted)] px-1 pt-1">Can't find it? Just ask Apex in the chat — it can help with most IT issues.</p>
              </div>
            </ScrollArea>
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
